import React, { useState, useEffect, useRef } from 'react';
import {
    Button,
    ButtonGroup,
    BodyText,
    Link,
    Spinner,
    Takeover
} from '@servicetitan/design-system';
import { ModalSizes } from '@servicetitan/design-system/dist/components/Modal/components';
import * as styles from './payment-method-modal.less';
import { paymentMethodsContainer } from 'src/payment-method/ioc';
import { ConnectedBankAccount, PaymentMethodType, PaymentMethodInfo } from 'src/invoice/types/types';
import { SavedPaymentItem } from '../../payment-methods/mobile/saved-payment-item';
import { PaymentMethods } from '../../payment-methods/desktop/payment-methods';
import { CardInfoFormState } from '../../card-info/card-info/card-info';
import { PGCardInfoFormState } from '../../card-info/pg-card-info/pg-card-info';
import { AchInfoFormState } from '../../ach-info/ach-info/ach-info';
import { PaymentGateway } from 'src/statement/stores/payment-gateway.store';
import { SavedPaymentMethodsStore } from 'src/payment-method/stores/saved-payment-methods.store';
import { ChargeType, SplitInfo } from 'src/api';

interface PaymentMethodModalProps {
    selectedPaymentMethodId?: number;
    onPaymentMethodSelected: (paymentMethod: PaymentMethodInfo) => void;
    onClose: () => void;
    isOpen: boolean;
    usePaymentGateway: () => boolean;
    usePaymentsService: boolean | undefined;
    invoiceId?: string;
    store: SavedPaymentMethodsStore;
    chargeType: ChargeType;
    splits?: SplitInfo[];
    statementId?: string;
}

enum PaymentMethodViewType {
    SelectPaymentMethod = 'select-payment-method',
    AddPaymentMethod = 'add-payment-method'
}

export const PaymentMethodModal: React.FC<PaymentMethodModalProps> = ({
    selectedPaymentMethodId,
    onPaymentMethodSelected,
    onClose,
    isOpen,
    usePaymentGateway,
    usePaymentsService,
    invoiceId,
    store,
    chargeType,
    splits,
    statementId
}) => {
    const paymentMethodType = useRef(PaymentMethodType.PayByBank);
    const creditCardInfo = useRef(new CardInfoFormState);
    const achInfo = useRef(new AchInfoFormState);
    const pgCreditCardInfo = useRef(new PGCardInfoFormState({ id: invoiceId || '', skipIFrameMessageHandler: true }));
    const [savePaymentChecked, setSavePaymentChecked] = useState(true);
    const [useAsPrimaryPaymentChecked, setUseAsPrimaryPaymentChecked] = useState(true);
    const [paymentMethodDialogTitle, setPaymentMethodDialogTitle] = useState('Select Payment Method');
    const [addPaymentMethodButtonTitle, setAddPaymentMethodButtonTitle] = useState('Add Card');
    const [paymentMethodViewType, setPaymentMethodViewType] = useState(PaymentMethodViewType.SelectPaymentMethod);
    const currentSelectedPaymentMethod = useRef<PaymentMethodInfo>();
    const [currentSelectedPaymentMethodId, setCurrentSelectedPaymentMethodId] = useState(selectedPaymentMethodId);
    const initialSelectedPaymentMethodId = useRef(selectedPaymentMethodId);
    const [isCurrentSelectedPaymentInValid, setIsCurrentSelectedPaymentInValid] = useState(false);

    const connectedAccounts = useRef<ConnectedBankAccount[]>([]);
    const paymentMethodAction = useRef<(() => void) | undefined>(undefined);

    const handleSelect = () => {
        handleSelectPaymentMethod(currentSelectedPaymentMethod.current)
    };

    const handleSelectPaymentMethod = (paymentMethod?: PaymentMethodInfo) => {
        if (paymentMethod) {
            // Update initial selected payment method if it is changed
            initialSelectedPaymentMethodId.current = paymentMethod.paymentMethodId;
            onPaymentMethodSelected(paymentMethod);
        }
        onClose();
    }


    const setPaymentMethodAction = (action: () => void) => {
        paymentMethodAction.current = action;
    };

    const paymentGateway = paymentMethodsContainer.get(PaymentGateway);
    const iframeMessageHandler = paymentGateway.iframeMessageHandler;
    React.useEffect(() => {
        window.addEventListener('message', iframeMessageHandler);
        return () => {
            window.removeEventListener('message', iframeMessageHandler);
        };
    });

    const handleAddPayment = () => {
        creditCardInfo.current = new CardInfoFormState;
        achInfo.current = new AchInfoFormState;
        pgCreditCardInfo.current = new PGCardInfoFormState({ id: invoiceId || '', skipIFrameMessageHandler: true });
        pgCreditCardInfo.current.desktopPaymentGatewayUrl = paymentGateway.getDesktopPaymentGatewayUrl(usePaymentsService);
        pgCreditCardInfo.current.iframeMessageHandler = iframeMessageHandler;
        setPaymentMethodViewType(PaymentMethodViewType.AddPaymentMethod);
    };

    const redirectToCardManagePage = () => {
        onClose();
        gotoSavedCardPage();
    }

    const {
        init,
        gotoSavedCardPage
    } = store;

    const [isLoadingSavedPaymentMethods, setIsLoadingSavedPaymentMethods] = useState(true);

    const preparePaymentOptions = (paymentMethodId: number | undefined) => {
        store.savedPaymentMethods?.forEach(paymentMethod => {
            paymentMethod.selected = paymentMethod.paymentMethodId === paymentMethodId;
            if (paymentMethod.selected) {
                currentSelectedPaymentMethod.current = paymentMethod;
                setIsCurrentSelectedPaymentInValid(store.isExpired(paymentMethod));
            }
        });
    };

    const updateDialogView = () => {
        setPaymentMethodDialogTitle(paymentMethodViewType === PaymentMethodViewType.AddPaymentMethod ?
            'Add Payment Method' : 'Select Payment Method');
    }

    const onBackForAddPaymentMethod = () => {
        setPaymentMethodViewType(PaymentMethodViewType.SelectPaymentMethod);
    }

    const onAddForAddPaymentMethod = async () => {
        if (!paymentMethodAction.current) {
            return;
        }

        // Pay-By-Bank after adding account action
        if (paymentMethodType.current === PaymentMethodType.PayByBank) {
            if (connectedAccounts.current.length > 0) {

                const connectedAccount = connectedAccounts.current
                    .filter(account => account.selected)[0];

                const payByBankPaymentMethod = store.connectedBankAccountToPaymentMethod(connectedAccount,
                    useAsPrimaryPaymentChecked && savePaymentChecked
                );

                addPaymentMethod(payByBankPaymentMethod);
                return;
            } else {
                paymentMethodAction.current();
            }
        }

        if (paymentMethodType.current === PaymentMethodType.CreditCard) {
            if (usePaymentGateway()) {
                const cardResult = await pgCreditCardInfo.current.form.validate();
                if (cardResult.hasError) {
                    return;
                }

                const paymentMethod = { paymentMethodId: 0 } as PaymentMethodInfo;
                paymentMethod.paymentMethodType = PaymentMethodType.CreditCard;

                addPaymentMethod(paymentMethod);
            } else {
                const paymentMethod = await store.cardInfoToPaymentMethod(creditCardInfo.current,
                    useAsPrimaryPaymentChecked && savePaymentChecked
                );

                if (!paymentMethod) {
                    return;
                }

                addPaymentMethod(paymentMethod);
            }
        }

        if (paymentMethodType.current === PaymentMethodType.ACH) {
            const achPaymentMethod = await store.achInfoToPaymentMethod(achInfo.current,
                useAsPrimaryPaymentChecked && savePaymentChecked
            );

            if (!achPaymentMethod) {
                return;
            }

            addPaymentMethod(achPaymentMethod);
        }

        // paymentMethodAction.current();
    };

    const addPaymentMethod = (paymentMethod: PaymentMethodInfo) => {
        // reset selected if newly added payment is selected
        if (paymentMethod.selected) {
            store.savedPaymentMethods?.forEach(paymentMethod => { paymentMethod.selected = false; });
        }

        // reset primary if newly added payment is primary
        if (paymentMethod.primary) {
            store.savedPaymentMethods?.forEach(paymentMethod => { paymentMethod.primary = false; });
        }

        if (savePaymentChecked) {
            store.setOnActionComplete(success => {
                if (!success) {
                    return;
                }

                const lastSavedPayment = store.getLastSavedPaymentMethod();
                currentSelectedPaymentMethod.current = lastSavedPayment;
                setCurrentSelectedPaymentMethodId(currentSelectedPaymentMethod.current?.paymentMethodId);

                handleSelectPaymentMethod(lastSavedPayment);
            });

            store.needToStoreAsPrimary = useAsPrimaryPaymentChecked;
            store.addPaymentMethod(paymentMethod);
        } else if (paymentMethod.paymentMethodType === PaymentMethodType.CreditCard && usePaymentGateway()) {
            store.processGatewayAddNewCard(p => {
                currentSelectedPaymentMethod.current = p;
                setCurrentSelectedPaymentMethodId(p!.paymentMethodId);
                handleSelect();
            });
        } else {
            currentSelectedPaymentMethod.current = paymentMethod;
            setCurrentSelectedPaymentMethodId(paymentMethod.paymentMethodId);
            handleSelect();
        }
    };

    const updateButtonTitleForPayByBank = () => {
        setAddPaymentMethodButtonTitle(connectedAccounts.current.length > 0 ?
            'Add Bank Account' : 'Connect Bank Account'
        );
    }

    const setPaymentMethodType = (type: PaymentMethodType) => {
        paymentMethodType.current = type;
        if (type === PaymentMethodType.CreditCard) {
            setAddPaymentMethodButtonTitle('Add Card');
        } else if (type === PaymentMethodType.ACH) {
            setAddPaymentMethodButtonTitle('Add ACH');
        } else {
            updateButtonTitleForPayByBank();
        }
    };

    const updateConnectedAccounts = (accounts: ConnectedBankAccount[]) => {
        connectedAccounts.current = accounts;
        updateButtonTitleForPayByBank();
    };

    const onModalClose = () => {
        if (initialSelectedPaymentMethodId.current) {
            // Set original selected payment if closing dialog without selection
            const initialPaymentMethod = store.savedPaymentMethods?.find(p => p.paymentMethodId === initialSelectedPaymentMethodId.current);
            if (initialPaymentMethod) {
                onPaymentMethodSelected(initialPaymentMethod);
            }
        }

        onClose();
    }

    useEffect(() => {
        store.currentInvoiceId = invoiceId;
        store.currentStatementId = statementId;
        store.currentSplits = splits;
        store.setOnSelectedPaymentMethodChange(() => {
            setIsLoadingSavedPaymentMethods(false);
        });
        init();
    }, [init]);

    useEffect(() => {
        preparePaymentOptions(currentSelectedPaymentMethodId);
    }, [currentSelectedPaymentMethodId, store.savedPaymentMethods]);

    useEffect(() => {
        pgCreditCardInfo.current.desktopPaymentGatewayUrl = paymentGateway.getDesktopPaymentGatewayUrl(usePaymentsService);
        pgCreditCardInfo.current.mobilePaymentGatewayUrl = paymentGateway.getMobilePaymentGatewayUrl(usePaymentsService);
        pgCreditCardInfo.current.iframeMessageHandler = iframeMessageHandler;
    }, []);

    useEffect(() => {
        updateDialogView();
    }, [paymentMethodViewType]);

    return (
        <Takeover
            size={ModalSizes.S}
            theme='light'
            open={isOpen}
            onClose={onModalClose}
            contentWrapperClass={styles.paymentMethodTakeover}
            className={styles.paymentMethodTakeover}
            closable
            title={
                <span className={styles.paymentMethodTakeoverHeader}>
                    {paymentMethodDialogTitle}
                </span>
            }
            footer={
                paymentMethodViewType === PaymentMethodViewType.SelectPaymentMethod ? (
                    <ButtonGroup className={styles.paymentMethodButtons}>
                        <Button onClick={handleAddPayment} className={styles.paymentMethodLeftButton}>
                            Add payment method
                        </Button>
                        <Button primary disabled={isCurrentSelectedPaymentInValid} onClick={handleSelect} className={styles.paymentMethodRightButton}>
                            Select
                        </Button>
                    </ButtonGroup>) :
                    (<ButtonGroup className={styles.paymentMethodButtons} >
                        <Button onClick={onBackForAddPaymentMethod} className={styles.paymentMethodLeftButton}>
                            Back
                        </Button>
                        <Button primary onClick={onAddForAddPaymentMethod} className={styles.paymentMethodRightButtonConnect}>
                            {addPaymentMethodButtonTitle}
                        </Button>
                    </ButtonGroup>)
            }
        >
            {paymentMethodViewType === PaymentMethodViewType.SelectPaymentMethod &&
                (<BodyText size="medium" className={styles.description}>
                    To manage your payment methods,{' '}
                    <Link color="primary" onClick={redirectToCardManagePage}>
                        click here
                    </Link>.
                </BodyText>)}
            {isLoadingSavedPaymentMethods ? (
                <div className={styles.fullHeight}>
                    <Spinner className={styles.spinner} />
                    <BodyText size="large" className={styles.loadingMessage}>
                        Loading saved payment methods...
                    </BodyText>
                </div>
            ) : (paymentMethodViewType === PaymentMethodViewType.SelectPaymentMethod ?
                (<div className={styles.paymentMethodCard}>
                    {
                        store.savedPaymentMethods?.map(
                            (paymentMethod, index) =>
                                <SavedPaymentItem
                                    key={paymentMethod.paymentMethodId}
                                    paymentMethod={paymentMethod}
                                    selected={currentSelectedPaymentMethodId === paymentMethod.paymentMethodId}
                                    showDivider={index !== store.savedPaymentMethods!.length - 1}
                                    onSelectPaymentMethod={paymentMethodId => {
                                        paymentMethod.selected = true;
                                        preparePaymentOptions(paymentMethod.paymentMethodId);
                                        setCurrentSelectedPaymentMethodId(paymentMethodId);
                                    }} />
                        )}
                </div>) : (
                    <PaymentMethods
                        cardInfoData={creditCardInfo.current}
                        chargeType={chargeType}
                        pgcardInfoData={pgCreditCardInfo.current}
                        invoiceId={invoiceId}
                        statementId={statementId}
                        splits={splits}
                        achInfoData={achInfo.current}
                        usePaymentGateway={usePaymentGateway}
                        showLoginLink={false}
                        setPaymentMethodValid={() => { }}
                        setPaymentMethodAction={setPaymentMethodAction}
                        setConnectedAccounts={updateConnectedAccounts}
                        setPaymentMethodType={setPaymentMethodType}
                        showPrimaryOption={store.getIsCPEnabled()}
                        showSavePaymentOption={store.getIsCPEnabled()}
                        savePaymentChecked={savePaymentChecked}
                        setSavePaymentChecked={setSavePaymentChecked}
                        useAsPrimaryPaymentChecked={useAsPrimaryPaymentChecked}
                        setUseAsPrimaryPaymentChecked={setUseAsPrimaryPaymentChecked}
                        paymentMethodStore={store}
                    />
                )
            )}
        </Takeover>
    );
}
